import * as React from 'react';

export interface OrderIconProps extends React.SVGProps<SVGSVGElement> {
  className?: string;
}

/**
 * Icons extracted verbatim from the Noor.Merchant Figma file (Eats Orders
 * screen, node 30:7346). Each glyph is the exact vector from the design; the
 * hardcoded fills are swapped for `currentColor` so callers control size
 * (`size-*`) and tint. Each keeps its native Figma viewBox.
 */

/** Magnifier — the orders search field's leading glyph (Figma "search", 20×20). */
export function OrderSearchIcon({ className, ...props }: OrderIconProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M16.2476 8.12381C16.2476 9.91652 15.6657 11.5725 14.6853 12.9161L19.6299 17.8646C20.1181 18.3528 20.1181 19.1456 19.6299 19.6338C19.1417 20.1221 18.3489 20.1221 17.8607 19.6338L12.9161 14.6853C11.5725 15.6696 9.91652 16.2476 8.12381 16.2476C3.63619 16.2476 0 12.6114 0 8.12381C0 3.63619 3.63619 0 8.12381 0C12.6114 0 16.2476 3.63619 16.2476 8.12381ZM8.12381 13.748C8.86239 13.748 9.59373 13.6025 10.2761 13.3199C10.9584 13.0372 11.5784 12.623 12.1007 12.1007C12.623 11.5784 13.0372 10.9584 13.3199 10.2761C13.6025 9.59373 13.748 8.86239 13.748 8.12381C13.748 7.38523 13.6025 6.65389 13.3199 5.97153C13.0372 5.28917 12.623 4.66917 12.1007 4.14692C11.5784 3.62466 10.9584 3.21039 10.2761 2.92775C9.59373 2.64511 8.86239 2.49963 8.12381 2.49963C7.38523 2.49963 6.65389 2.64511 5.97153 2.92775C5.28917 3.21039 4.66917 3.62466 4.14692 4.14692C3.62466 4.66917 3.21039 5.28917 2.92775 5.97153C2.64511 6.65389 2.49963 7.38523 2.49963 8.12381C2.49963 8.86239 2.64511 9.59373 2.92775 10.2761C3.21039 10.9584 3.62466 11.5784 4.14692 12.1007C4.66917 12.623 5.28917 13.0372 5.97153 13.3199C6.65389 13.6025 7.38523 13.748 8.12381 13.748Z" />
    </svg>
  );
}

/** Calendar — the date-range pill's leading glyph (Figma "calendar", 16×16). */
export function OrderCalendarIcon({ className, ...props }: OrderIconProps) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M3.88889 1V2H2.38889C1.56076 2 0.888889 2.67188 0.888889 3.5V5H14.8889V3.5C14.8889 2.67188 14.217 2 13.3889 2H11.8889V1C11.8889 0.446875 11.442 0 10.8889 0C10.3358 0 9.88889 0.446875 9.88889 1V2H5.88889V1C5.88889 0.446875 5.44201 0 4.88889 0C4.33576 0 3.88889 0.446875 3.88889 1ZM14.8889 6H0.888889V14.5C0.888889 15.3281 1.56076 16 2.38889 16H13.3889C14.217 16 14.8889 15.3281 14.8889 14.5V6Z" />
    </svg>
  );
}

/** Close (X) — the detail panel's dismiss glyph (Figma "close bold", 24×24). */
export function OrderCloseIcon({ className, ...props }: OrderIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 14.122L17.303 19.425C17.5844 19.7064 17.966 19.8645 18.364 19.8645C18.762 19.8645 19.1436 19.7064 19.425 19.425C19.7064 19.1436 19.8645 18.762 19.8645 18.364C19.8645 17.966 19.7064 17.5844 19.425 17.303L14.12 12L19.424 6.697C19.5633 6.55767 19.6737 6.39227 19.7491 6.21025C19.8244 6.02823 19.8632 5.83315 19.8631 5.63615C19.8631 5.43915 19.8242 5.24409 19.7488 5.0621C19.6734 4.88011 19.5628 4.71477 19.4235 4.5755C19.2842 4.43623 19.1188 4.32577 18.9367 4.25043C18.7547 4.17508 18.5596 4.13633 18.3626 4.13637C18.1656 4.13642 17.9706 4.17527 17.7886 4.2507C17.6066 4.32613 17.4413 4.43667 17.302 4.576L12 9.879L6.697 4.576C6.5587 4.43267 6.39323 4.31832 6.21027 4.23962C6.0273 4.16092 5.83049 4.11945 5.63132 4.11763C5.43215 4.1158 5.23462 4.15366 5.05024 4.229C4.86586 4.30433 4.69833 4.41563 4.55742 4.55641C4.41652 4.69718 4.30506 4.8646 4.22955 5.04891C4.15404 5.23322 4.116 5.43072 4.11763 5.62989C4.11927 5.82906 4.16056 6.02591 4.23908 6.20895C4.31761 6.39199 4.4318 6.55756 4.575 6.696L9.88 12L4.576 17.304C4.4328 17.4424 4.31861 17.608 4.24008 17.7911C4.16156 17.9741 4.12027 18.1709 4.11863 18.3701C4.117 18.5693 4.15504 18.7668 4.23055 18.9511C4.30606 19.1354 4.41752 19.3028 4.55842 19.4436C4.69933 19.5844 4.86686 19.6957 5.05124 19.771C5.23562 19.8463 5.43315 19.8842 5.63232 19.8824C5.83149 19.8805 6.0283 19.8391 6.21127 19.7604C6.39423 19.6817 6.5597 19.5673 6.698 19.424L12 14.123V14.122Z"
      />
    </svg>
  );
}

/**
 * Scooter — the courier-handoff confirmation glyph (Figma "Scooter Icon",
 * node 840:39156). Drawn on a 60×48 box; the design tints it orange, so
 * callers pass `text-primary`.
 */
export function OrderScooterIcon({ className, ...props }: OrderIconProps) {
  return (
    <svg
      viewBox="0 0 60 48"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M39 3C39 1.34315 40.3431 0 42 0C50.2843 0 57 6.71573 57 15V21C57 22.6569 55.6569 24 54 24H48C41.3726 24 36 29.3726 36 36C36 37.6569 34.6569 39 33 39H24C24 43.9705 19.9706 48 15 48C10.0294 48 6.00001 43.9705 6 39H3C1.34315 39 0 37.6569 0 36V33C0 24.7157 6.71573 18 15 18H21C22.6569 18 24 19.3431 24 21V33H30.2489C31.6771 24.4869 39.081 18 48 18H51V15C51 10.0294 46.9706 6 42 6C40.3431 6 39 4.65685 39 3Z"
      />
      <path d="M9 6C7.34315 6 6 7.34315 6 9C6 10.6569 7.34315 12 9 12H21C22.6569 12 24 10.6569 24 9C24 7.34315 22.6569 6 21 6H9Z" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M51 30C46.0294 30 42 34.0294 42 39C42 43.9706 46.0294 48 51 48C55.9706 48 60 43.9706 60 39C60 34.0294 55.9706 30 51 30Z"
      />
    </svg>
  );
}
